import React, { createContext, useContext, useState, useEffect } from 'react';

export type UserRole = 'viewer' | 'admin';

interface AuthContextType {
  isAuthenticated: boolean;
  userRole: UserRole;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Server-side authentication - no passwords stored client-side

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>('viewer');

  useEffect(() => {
    // Check if user was previously authenticated
    const savedAuth = localStorage.getItem('record-collection-auth');
    const savedRole = localStorage.getItem('record-collection-role') as UserRole;
    
    if (savedAuth === 'authenticated' && (savedRole === 'viewer' || savedRole === 'admin')) {
      setIsAuthenticated(true);
      setUserRole(savedRole);
    }
  }, []);

  const login = async (password: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (data.valid && data.role) {
        setIsAuthenticated(true);
        setUserRole(data.role);
        localStorage.setItem('record-collection-auth', 'authenticated');
        localStorage.setItem('record-collection-role', data.role);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Authentication error:', error);
      return false;
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUserRole('viewer');
    localStorage.removeItem('record-collection-auth');
    localStorage.removeItem('record-collection-role');
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userRole,
        login,
        logout,
        isAdmin: userRole === 'admin'
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};